import { Button, buttonMaxWidth } from '@/src/common/components/Button'
import { ScrollView, Text } from 'react-native'
import { usePaymentScreen } from '@/src/experiences/card-payments/hooks/usePaymentScreen'
import { useLocalSearchParams } from 'expo-router'
import { z } from 'zod/v4'
import { DesignSystem } from '@/src/design-system'
import { Detail } from '@/src/design-system/Detail'
import { mesaColorNames, typography } from '@/src/common/theme'
import { PaymentIcon } from '@/src/assets/icons/PaymentIcon'
import type { FunctionComponent } from 'react'
import { Spinner } from 'tamagui'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import * as CardActivity from '@/src/experiences/card-activity'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import { LegacyOneTimePaymentScheduleReview } from '@/src/experiences/card-payments/screens/OnetimePayScheduleReview/LegacyOnetimePayScheduleReview'
import { PlatformOneTimePaymentScheduleReview } from '@/src/experiences/card-payments/screens/OnetimePayScheduleReview/PlatformOnetimePayScheduleReview'

export function useOneTimePaymentScheduleReviewScreenProps() {
  return usePaymentScreen(
    'oneTimePayment/[id]',
    'Review One-time Payment',
    '/main/card/card-management/payment',
  )
}

const Params = z.object({ id: z.string() })

/** @example July 1, 2025 */
export function formatOneTimePaymentDate(dateString: string) {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

type OneTimePaymentScheduleReviewScreenProps = ReturnType<
  typeof useOneTimePaymentScheduleReviewScreenProps
>

export type OneTimePaymentScheduleReviewComponent = FunctionComponent<
  {
    oneTimePaymentScheduleReviewScreenProps: OneTimePaymentScheduleReviewScreenProps
    initialized?: boolean
    loading?: boolean
    disabled?: boolean
    cancel?: () => void
    data?: {
      amount: string
      status?: string | null
      date?: string
      account?: string
    }
  } & z.infer<typeof Params>
>

export type OneTimePaymentScheduleReviewComponentWrapper = FunctionComponent<
  {
    OneTimePaymentScheduleReview: OneTimePaymentScheduleReviewComponent
    oneTimePaymentScheduleReviewScreenProps: OneTimePaymentScheduleReviewScreenProps
  } & z.infer<typeof Params>
>

export default provideCardPlatform<OneTimePaymentScheduleReviewComponent>(
  ({ platform, ToWrap }) => {
    const oneTimePaymentScheduleReviewScreenProps = useOneTimePaymentScheduleReviewScreenProps()
    const { id } = Params.parse(useLocalSearchParams())
    switch (platform) {
      case 'HIGHNOTE':
        return (
          <LegacyOneTimePaymentScheduleReview
            OneTimePaymentScheduleReview={ToWrap}
            oneTimePaymentScheduleReviewScreenProps={oneTimePaymentScheduleReviewScreenProps}
            id={id}
          />
        )
      case 'LOANPRO':
        return (
          <PlatformOneTimePaymentScheduleReview
            OneTimePaymentScheduleReview={ToWrap}
            oneTimePaymentScheduleReviewScreenProps={oneTimePaymentScheduleReviewScreenProps}
            id={id}
          />
        )
      default:
        platform satisfies undefined
        return (
          <ToWrap
            oneTimePaymentScheduleReviewScreenProps={oneTimePaymentScheduleReviewScreenProps}
            id={id}
            loading
          />
        )
    }
  },
)(function OneTimePaymentScheduleReview({
  oneTimePaymentScheduleReviewScreenProps: { Modal },
  initialized,
  loading,
  disabled,
  cancel,
  data,
}) {
  return Modal({
    children: !initialized ? (
      <Spinner
        size="large"
        style={{ marginTop: 32, marginBottom: 32, marginLeft: 'auto', marginRight: 'auto' }}
        color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
      />
    ) : !data ? (
      <>
        <CardActivity.Components.Highnote.NoTransactionsIcon
          svgProps={{ style: { marginHorizontal: 'auto', marginTop: 32 } }}
          pathProps={{ stroke: mesaColorNames['Mesa - Brand/White #FFFFFF'] }}
        />
        <Text
          style={[
            typography.L,
            {
              textAlign: 'center',
              color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
              marginTop: 32,
              marginBottom: 32,
            },
          ]}
        >
          Scheduled One-time Payment not found
        </Text>
      </>
    ) : (
      <ScrollView
        contentContainerStyle={{
          paddingBottom: 50,
          gap: 32,
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <DesignSystem.Card
          style={{
            alignSelf: 'stretch',
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 16,
            paddingBottom: 16,
            marginHorizontal: 16,
            gap: 8,
          }}
        >
          <DesignSystem.BoxedIcon Icon={PaymentIcon} />

          <Text
            style={[
              { color: mesaColorNames['global/grey/70 #53575A'], marginVertical: 16 },
              typography['label/xs-bold'],
            ]}
          >
            Scheduled One-time Payment Details
          </Text>

          <Detail category="Transfer Amount" description={data.amount} />
          <Detail category="Status" description={data.status} />
          <Detail category="Scheduled Transfer Date" description={data.date} />
          <Detail category="Bank Account" description={data.account} />
        </DesignSystem.Card>
        <Button
          title="Cancel scheduled One-time Payment"
          testID={CardPaymentsConstants.Ids.Press.CancelOnetimePay}
          style={{
            minWidth: buttonMaxWidth,
            maxWidth: 'none',
            width: 'auto',
            marginLeft: 16,
            marginRight: 16,
            paddingLeft: 16,
            paddingRight: 16,
          }}
          {...(disabled ? { disabled, loading } : { onPress: cancel })}
        />
      </ScrollView>
    ),
  })
})
